import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { DeleteOutline as DeleteOutlineIcon } from '@mui/icons-material';
import {
  PREINVOICE_STATUSES,
  buildPaymentReminderDraft,
  deletePreinvoiceEntry,
  sendPaymentReminderEmail,
  subscribeToRecentPreinvoices,
  updatePreinvoiceStatus
} from '../utils/preinvoiceUtils';
import {
  INVOICE_STATUSES,
  deleteInvoiceEntry,
  subscribeToRecentInvoices,
  updateInvoiceStatus
} from '../utils/invoiceUtils';
import { recordClientMoneyReceived } from '../utils/accountingUtils';

const todayIso = () => new Date().toISOString().slice(0, 10);

const LIST_TYPES = {
  preinvoice: {
    key: 'preinvoice',
    label: 'Išankstinės sąskaitos',
    title: 'Išankstinės sąskaitos',
    statuses: PREINVOICE_STATUSES,
    updateStatus: updatePreinvoiceStatus,
    deleteEntry: deletePreinvoiceEntry
  },
  invoice: {
    key: 'invoice',
    label: 'Sąskaitos faktūros',
    title: 'Sąskaitos faktūros',
    statuses: INVOICE_STATUSES,
    updateStatus: updateInvoiceStatus,
    deleteEntry: deleteInvoiceEntry
  }
};

export default function InvoiceDocumentsList({
  enabled,
  currentYear,
  readOnly = false,
  headerTitle = 'Išsiųstos sąskaitos',
  emptyHint = 'Dar nėra įrašų.',
  preinvoiceSubscribe = subscribeToRecentPreinvoices,
  invoiceSubscribe = subscribeToRecentInvoices
}) {
  const [listType, setListType] = useState('preinvoice');
  const [preinvoices, setPreinvoices] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [error, setError] = useState('');
  const [statusUpdatingId, setStatusUpdatingId] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deletingId, setDeletingId] = useState('');
  const [remindItem, setRemindItem] = useState(null);
  const [remindSubject, setRemindSubject] = useState('');
  const [remindBody, setRemindBody] = useState('');
  const [remindTo, setRemindTo] = useState('');
  const [remindConfirmOpen, setRemindConfirmOpen] = useState(false);
  const [remindSending, setRemindSending] = useState(false);
  const [remindSuccess, setRemindSuccess] = useState('');
  const [moneyItem, setMoneyItem] = useState(null);
  const [moneyAmount, setMoneyAmount] = useState('');
  const [moneyDate, setMoneyDate] = useState(todayIso());
  const [moneyNote, setMoneyNote] = useState('');
  const [moneySaving, setMoneySaving] = useState(false);
  const [moneySuccess, setMoneySuccess] = useState('');

  useEffect(() => {
    if (!enabled) {
      setPreinvoices([]);
      setInvoices([]);
      return undefined;
    }
    const unsubPre = preinvoiceSubscribe(currentYear, setPreinvoices);
    const unsubInv = invoiceSubscribe(currentYear, setInvoices);
    return () => {
      unsubPre();
      unsubInv();
    };
  }, [currentYear, enabled, preinvoiceSubscribe, invoiceSubscribe]);

  const config = LIST_TYPES[listType];
  const items = listType === 'preinvoice' ? preinvoices : invoices;

  const handleStatusChange = async (docId, status) => {
    setStatusUpdatingId(docId);
    try {
      await config.updateStatus(currentYear, docId, status);
    } catch (err) {
      setError(err.message || 'Nepavyko pakeisti statuso.');
    } finally {
      setStatusUpdatingId('');
    }
  };

  const openDeleteDialog = (item) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleDeleteConfirmed = async () => {
    if (!itemToDelete) return;
    const deletingItem = itemToDelete;
    setDeletingId(deletingItem.id);
    setDeleteDialogOpen(false);
    setItemToDelete(null);
    try {
      await config.deleteEntry(currentYear, deletingItem);
    } catch (err) {
      setError(err.message || 'Nepavyko ištrinti sąskaitos.');
    } finally {
      setDeletingId('');
    }
  };

  const openRemindDialog = (item) => {
    const draft = buildPaymentReminderDraft(listType, item);
    setRemindItem(item);
    setRemindSubject(draft.subject);
    setRemindBody(draft.body);
    setRemindTo(draft.toEmails.join(', '));
    setRemindSuccess('');
    setError('');
  };

  const closeRemindDialog = () => {
    if (remindSending) return;
    setRemindConfirmOpen(false);
    setRemindItem(null);
    setRemindSubject('');
    setRemindBody('');
    setRemindTo('');
  };

  const parseRemindToEmails = () => remindTo
    .split(/[,;\s]+/)
    .map((e) => e.trim().toLowerCase())
    .filter((e) => e.includes('@'));

  const handleSendReminderConfirmed = async () => {
    if (!remindItem) return;
    const draft = buildPaymentReminderDraft(listType, remindItem);
    const toEmails = parseRemindToEmails();

    setRemindSending(true);
    setError('');
    setRemindSuccess('');
    try {
      await sendPaymentReminderEmail({
        toEmails,
        subject: remindSubject,
        body: remindBody,
        pdfUrl: draft.pdfUrl || remindItem.pdfUrl,
        fileName: draft.fileName,
        clientName: draft.clientName,
        documentId: remindItem.id
      });
      setRemindSuccess('Priminimo laiškas įtrauktas į siuntimo eilę.');
      setRemindConfirmOpen(false);
      setRemindItem(null);
      setRemindSubject('');
      setRemindBody('');
      setRemindTo('');
    } catch (err) {
      setRemindConfirmOpen(false);
      setError(err.message || 'Nepavyko išsiųsti priminimo.');
    } finally {
      setRemindSending(false);
    }
  };

  const canSendReminder = Boolean(
    remindItem
    && remindSubject.trim()
    && remindBody.trim()
    && parseRemindToEmails().length > 0
    && remindItem.pdfUrl
  );

  const remindConfirmSummary = remindItem
    ? `Siųsti priminimą apie apmokėjimą (${remindItem.invoiceNumber || remindItem.id}, ${Number(remindItem.amountEur || 0).toFixed(2)} Eur) adresu: ${parseRemindToEmails().join(', ') || '—'}? Prie laiško bus pridėtas PDF.`
    : '';

  const openMoneyDialog = (item) => {
    setMoneyItem(item);
    setMoneyAmount(Number(item.amountEur || 0).toFixed(2));
    setMoneyDate(todayIso());
    setMoneyNote('');
    setMoneySuccess('');
    setError('');
  };

  const closeMoneyDialog = () => {
    if (moneySaving) return;
    setMoneyItem(null);
    setMoneyAmount('');
    setMoneyNote('');
  };

  const handleMoneyReceived = async () => {
    if (!moneyItem) return;
    setMoneySaving(true);
    setError('');
    setMoneySuccess('');
    try {
      await recordClientMoneyReceived({
        listType,
        item: moneyItem,
        year: currentYear,
        amountEur: moneyAmount,
        date: moneyDate,
        note: moneyNote
      });
      setMoneySuccess(
        `Į apskaitą įrašyta +${Number(moneyAmount).toFixed(2)} Eur (${moneyItem.clientName || moneyItem.clientEmail || 'klientas'}).`
      );
      setMoneyItem(null);
      setMoneyAmount('');
      setMoneyNote('');
    } catch (err) {
      setError(err.message || 'Nepavyko įrašyti gautų pinigų.');
    } finally {
      setMoneySaving(false);
    }
  };

  const canSaveMoney = Boolean(
    moneyItem
    && Number(moneyAmount) > 0
    && /^\d{4}-\d{2}-\d{2}$/.test(moneyDate)
  );

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {remindSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRemindSuccess('')}>
          {remindSuccess}
        </Alert>
      )}
      {moneySuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMoneySuccess('')}>
          {moneySuccess}
        </Alert>
      )}

      <Paper sx={{ p: 2.5, mb: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {headerTitle}
          </Typography>
          <ButtonGroup variant="outlined" size="small">
            {Object.values(LIST_TYPES).map((type) => (
              <Button
                key={type.key}
                variant={listType === type.key ? 'contained' : 'outlined'}
                onClick={() => setListType(type.key)}
              >
                {type.label}
              </Button>
            ))}
          </ButtonGroup>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {config.title} — naujausi viršuje.
        </Typography>

        <Stack spacing={1}>
          {items.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {emptyHint}
            </Typography>
          )}
          {items.map((item) => (
            <Paper
              key={item.id}
              variant="outlined"
              sx={{
                p: 1.5,
                borderColor: item.isTest ? 'warning.main' : 'divider',
                borderWidth: item.isTest ? 2 : 1
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Box>
                  {!readOnly && (
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {item.invoiceForCompany && item.companyName
                        ? `${item.companyName} (${item.clientName || 'Klientas'})`
                        : `${item.clientName || 'Klientas'} (${item.clientEmail})`}
                    </Typography>
                  )}
                  {readOnly && (
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {item.invoiceNumber || item.id}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {!readOnly && `${item.invoiceNumber || item.id} | `}
                    {Number(item.amountEur || 0).toFixed(2)} Eur
                    {item.invoiceDate ? ` | ${item.invoiceDate}` : ''}
                    {!readOnly && item.invoiceForCompany ? ' | Įmonei' : ''}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    size="small"
                    label={item.status || '—'}
                    color={item.status === 'sendError' ? 'error' : item.status === 'Payed' ? 'success' : 'default'}
                  />
                  {!readOnly && item.emailSent === false && (
                    <Chip size="small" label="Be el. laiško" variant="outlined" />
                  )}
                  {!readOnly && (
                    <Select
                      size="small"
                      value={item.status || 'pending payment'}
                      disabled={statusUpdatingId === item.id}
                      onChange={(e) => handleStatusChange(item.id, e.target.value)}
                    >
                      {config.statuses.map((st) => (
                        <MenuItem key={st} value={st}>{st}</MenuItem>
                      ))}
                    </Select>
                  )}
                  {!readOnly && item.status === 'pending payment' && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      onClick={() => openRemindDialog(item)}
                      disabled={!item.pdfUrl || statusUpdatingId === item.id || deletingId === item.id}
                    >
                      Priminti
                    </Button>
                  )}
                  {!readOnly && item.status !== 'sendError' && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      onClick={() => openMoneyDialog(item)}
                      disabled={statusUpdatingId === item.id || deletingId === item.id}
                    >
                      +Gauti pinigai
                    </Button>
                  )}
                  {item.pdfUrl && (
                    <Button size="small" href={item.pdfUrl} target="_blank" rel="noopener noreferrer">
                      PDF
                    </Button>
                  )}
                  {!readOnly && (
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteOutlineIcon />}
                      onClick={() => openDeleteDialog(item)}
                      disabled={statusUpdatingId === item.id || deletingId === item.id}
                    >
                      Ištrinti
                    </Button>
                  )}
                </Box>
              </Box>
              {item.sendError && (
                <Typography variant="caption" color="error.main">
                  sendError: {item.sendError}
                </Typography>
              )}
            </Paper>
          ))}
        </Stack>
      </Paper>

      {!readOnly && (
        <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
          <DialogTitle>Ištrinti sąskaitą?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Bus ištrinti sąskaitos įrašas, susiję `mail` laiškai ir PDF failas.
            </Typography>
            {itemToDelete && (
              <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                {itemToDelete.clientName || 'Klientas'} ({itemToDelete.clientEmail || 'be el. pašto'}) — {itemToDelete.invoiceNumber || itemToDelete.id}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDeleteDialog} disabled={Boolean(deletingId)}>Atšaukti</Button>
            <Button color="error" variant="contained" onClick={handleDeleteConfirmed} disabled={Boolean(deletingId)}>
              Ištrinti
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {!readOnly && (
        <Dialog
          open={Boolean(remindItem)}
          onClose={closeRemindDialog}
          fullWidth
          maxWidth="sm"
          PaperProps={{ sx: { maxHeight: '90vh' } }}
        >
          <DialogTitle>Priminti apie apmokėjimą</DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Redaguokite laišką prieš siunčiant. Prie laiško bus pridėtas tas pats sąskaitos PDF.
              Žymeklis <code>{'{{pdfUrl}}'}</code> laiške taps mygtuku „Atsisiųsti sąskaitą (PDF)“.
            </Typography>
            {remindItem && (
              <Typography variant="body2" color="text.secondary">
                {remindItem.invoiceNumber || remindItem.id}
                {' · '}
                {Number(remindItem.amountEur || 0).toFixed(2)} Eur
                {' · '}
                {listType === 'preinvoice' ? 'Išankstinė' : 'Sąskaita faktūra'}
              </Typography>
            )}
            <TextField
              label="Gavėjai"
              value={remindTo}
              onChange={(e) => setRemindTo(e.target.value)}
              fullWidth
              size="small"
              helperText="Keli adresai atskiriami kableliu"
              disabled={remindSending}
            />
            <TextField
              label="Tema"
              value={remindSubject}
              onChange={(e) => setRemindSubject(e.target.value)}
              fullWidth
              size="small"
              disabled={remindSending}
            />
            <TextField
              label="Laiško tekstas"
              value={remindBody}
              onChange={(e) => setRemindBody(e.target.value)}
              multiline
              minRows={10}
              fullWidth
              disabled={remindSending}
              inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
            />
            {remindItem && !remindItem.pdfUrl && (
              <Alert severity="warning">Šiai sąskaitai nėra PDF — priminimo išsiųsti negalima.</Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeRemindDialog} disabled={remindSending}>Atšaukti</Button>
            <Button
              variant="contained"
              color="warning"
              onClick={() => setRemindConfirmOpen(true)}
              disabled={!canSendReminder || remindSending}
            >
              Siųsti priminimą
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {!readOnly && (
        <Dialog open={Boolean(moneyItem)} onClose={closeMoneyDialog} fullWidth maxWidth="xs">
          <DialogTitle>+Gauti pinigai</DialogTitle>
          <DialogContent>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Įrašoma į kliento balansą kaip gauti pinigai (neprivaloma lygi sąskaitos sumai).
                {moneyItem && (
                  <>
                    {' '}
                    Dokumentas: <strong>{moneyItem.invoiceNumber || moneyItem.id}</strong>
                    {' · '}
                    sąskaitos suma {Number(moneyItem.amountEur || 0).toFixed(2)} Eur.
                  </>
                )}
              </Typography>
              <TextField
                label="Gauta suma Eur"
                type="number"
                size="small"
                value={moneyAmount}
                onChange={(e) => setMoneyAmount(e.target.value)}
                fullWidth
                inputProps={{ step: '0.01', min: '0.01' }}
                disabled={moneySaving}
                autoFocus
              />
              <TextField
                label="Gavimo data"
                type="date"
                size="small"
                value={moneyDate}
                onChange={(e) => setMoneyDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                disabled={moneySaving}
              />
              <TextField
                label="Pastaba (neprivaloma)"
                size="small"
                value={moneyNote}
                onChange={(e) => setMoneyNote(e.target.value)}
                fullWidth
                disabled={moneySaving}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeMoneyDialog} disabled={moneySaving}>Atšaukti</Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleMoneyReceived}
              disabled={!canSaveMoney || moneySaving}
            >
              {moneySaving ? 'Saugoma…' : 'Įrašyti'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {!readOnly && (
        <Dialog
          open={remindConfirmOpen}
          onClose={() => {
            if (remindSending) return;
            setRemindConfirmOpen(false);
          }}
        >
          <DialogTitle>Patvirtinimas</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              {remindConfirmSummary}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setRemindConfirmOpen(false)}
              disabled={remindSending}
            >
              Atšaukti
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handleSendReminderConfirmed}
              disabled={!canSendReminder || remindSending}
            >
              {remindSending ? 'Siunčiama…' : 'Patvirtinti ir siųsti'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
